import type { TaskView } from '@aj/core';
import { Chip, CompletionToggle, HStack, Text, VStack, cx } from '@aj/ui';

import { useT } from '../../providers/I18nProvider.js';

export type TaskListProps = {
  tasks: readonly TaskView[];
  /** Open tasks past the Home cap. Counted, never listed (§4). */
  hiddenCount: number;
  label: string;
  onToggle: (taskId: string) => void;
};

/**
 * Today's work: what's still open, then what's already done.
 *
 * Deliberately flat — no lists, no sections, no drag. A task is one line and a
 * circle, and §4 is explicit that this must not grow into project management.
 * The one structure it does keep is order: overdue first, then open, then
 * today's finished work at the bottom, which is the order the selector hands
 * them over in.
 *
 * Finished tasks stay until the day turns over, like completed habits fold in
 * place rather than vanishing.
 */
export function TaskList({ tasks, hiddenCount, label, onToggle }: TaskListProps) {
  const t = useT();

  return (
    <VStack gap={1} as="ul" aria-label={label}>
      {tasks.map((task) => (
        <HStack key={task.id} as="li" gap={3} align="center" className="min-h-11">
          <CompletionToggle
            checked={task.done}
            onChange={() => onToggle(task.id)}
            label={task.name}
          />

          <Text
            variant="body"
            tone={task.done ? 'subtle' : 'ink'}
            className={cx('min-w-0 flex-1 truncate', task.done && 'line-through')}
          >
            {task.name}
          </Text>

          {/* Overdue is the only status worth a badge: everything else on this
              list is simply "today", and a chip on every row is wallpaper. */}
          {task.overdue && (
            <Chip tone="danger" className="shrink-0">
              {t('home.overdue')}
            </Chip>
          )}
          {task.track && (
            <Text variant="caption" tone="subtle" className="shrink-0">
              {task.track}
            </Text>
          )}
        </HStack>
      ))}

      {hiddenCount > 0 && (
        <Text variant="caption" tone="subtle" className="pt-1 pl-10">
          {t('home.moreTasks', { count: hiddenCount })}
        </Text>
      )}
    </VStack>
  );
}
